import { AutocompleteRenderGetTagProps, Box, Collapse, GridLegacy as Grid, IconButton, Typography } from '@mui/material'
import { C1Chip } from 'c1ui/components/C1Chip'
import { C1AutocompleteMultipleAsyncHook, C1AutocompleteSingleAsyncHook } from 'components/core/autocomplete'
import { C1Link } from 'components/core/links/C1Link'
import { routes } from 'lib/routes'
import { RequestCatalog, SearchRequestCatalogsRequest } from 'pbts/c1/api/requestcatalog/v1/requestcatalog'
import { ComponentProps, FC, HTMLAttributes, ReactNode, useCallback, useState } from 'react'
import { useIntl } from 'react-intl'
import { requestCatalogsSearchApi } from 'redux_toolkit/request-catalogs'
import { CatalogsStyles } from './catalogs-styles'

type DisplayCatalogsProps = {
    proposedOptions: Array<RequestCatalog | undefined>
    emptySelectState?: ReactNode
    expandable?: boolean
    unExpandedLimit?: number
}

export const DisplayCatalogs: FC<DisplayCatalogsProps> = ({
    proposedOptions,
    emptySelectState,
    expandable = false,
    unExpandedLimit = 3,
}): React.ReactElement => {
    const [expanded, setExpanded] = useState(false)

    const slice = expandable && !expanded ? unExpandedLimit : proposedOptions.length

    if (proposedOptions.length === 0) {
        return (
            <Grid item sx={CatalogsStyles.empty}>
                {emptySelectState}
            </Grid>
        )
    }
    const CatalogList: FC = () => {
        return (
            <>
                {proposedOptions
                    .slice(0) // Make a copy. proposedOptions is read-only.
                    .sort((a, b) => {
                        // Sort alphabetically
                        const textA = a ? a.displayName.toUpperCase() : ''
                        const textB = b ? b.displayName.toUpperCase() : ''
                        return textA < textB ? -1 : textA > textB ? 1 : 0
                    })
                    .slice(0, slice)
                    .map((catalog, index) => {
                        if (catalog) {
                            const length = slice > 0 && proposedOptions.length > slice ? slice : proposedOptions.length
                            return (
                                <Box key={catalog.id} sx={CatalogsStyles.catalogItem}>
                                    <C1Link href={routes.accessProfiles.detail(catalog.id)}>
                                        {catalog.displayName}
                                    </C1Link>
                                    {index < length - 1 && <>,&nbsp;</>}
                                </Box>
                            )
                        }
                        return null
                    })}
            </>
        )
    }

    return (
        <>
            {expandable && proposedOptions.length > unExpandedLimit ? (
                <Box sx={CatalogsStyles.expandableCatalogListContainer}>
                    <Collapse in={expanded} collapsedSize="1.3em" sx={CatalogsStyles.catalogList}>
                        <CatalogList />
                    </Collapse>
                    {proposedOptions.length > unExpandedLimit && !expanded && (
                        <IconButton
                            size="small"
                            color="primary"
                            aria-label="Show more catalogs"
                            onClick={(): void => {
                                setExpanded(true)
                            }}
                            sx={CatalogsStyles.collapseCount}
                        >
                            <Typography component="span">
                                +
                                {proposedOptions.length - unExpandedLimit}
                            </Typography>
                        </IconButton>
                    )}
                </Box>
            ) : (
                <Box sx={CatalogsStyles.expandableCatalogListContainer}>
                    <CatalogList />
                </Box>
            )}
        </>
    )
}

type SearchRequestCatalogProps = Omit<
    ComponentProps<typeof C1AutocompleteMultipleAsyncHook<SearchRequestCatalogsRequest, RequestCatalog>>,
    'initialValue' | 'renderLabel' | 'renderOption' | 'hook' | 'Req'
> & {
    catalogs?: RequestCatalog[]
}

export const SearchCatalogs: FC<SearchRequestCatalogProps> = ({ catalogs: initialValue, ...rest }) => {
    const renderLabel = useCallback(
        (option: RequestCatalog): string => option?.displayName || option.id,
        [],
    )

    const renderTags = (value: RequestCatalog[], getTagProps: AutocompleteRenderGetTagProps): React.ReactElement[] =>
        value.map((option, index) => {
            const { key, ...props } = getTagProps({ index })
            return <C1Chip key={key} color="primary" label={renderLabel(option)} {...props} />
        })

    const renderOption = useCallback((htmlProps: HTMLAttributes<HTMLLIElement>, option: RequestCatalog): React.ReactElement => {
        return (
            <li {...htmlProps} key={option.id}>
                {option.displayName}
            </li>
        )
    }, [])

    const sortedCatalogs = initialValue
        ?.slice(0) // Make a copy. proposedOptions is read-only.
        .sort((a, b) => {
            // Sort alphabetically
            const textA = a ? a.displayName.toUpperCase() : ''
            const textB = b ? b.displayName.toUpperCase() : ''
            return textA < textB ? -1 : textA > textB ? 1 : 0
        })

    return (
        <C1AutocompleteMultipleAsyncHook<SearchRequestCatalogsRequest, RequestCatalog>
            {...rest}
            initialValue={sortedCatalogs}
            hook={requestCatalogsSearchApi.useLazySearchRequestCatalogs}
            Req={SearchRequestCatalogsRequest}
            renderOption={renderOption}
            renderLabel={renderLabel}
            renderTags={renderTags}
        />
    )
}

type SelectOneAccessProfileProps = Omit<
    ComponentProps<typeof C1AutocompleteSingleAsyncHook<SearchRequestCatalogsRequest, RequestCatalog>>,
    'initialValue' | 'renderLabel' | 'renderOption' | 'hook' | 'Req'
> & {
    hideLabel?: boolean
}

export const SelectOneAccessProfileHook: FC<SelectOneAccessProfileProps> = (props) => {
    const intl = useIntl()

    const renderLabel = useCallback(
        (option: RequestCatalog): string => option?.displayName || option.id,
        [],
    )

    const renderOption = useCallback((htmlProps: HTMLAttributes<HTMLLIElement>, option: RequestCatalog): React.ReactElement => {
        return (
            <li {...htmlProps} key={option.id}>
                {option.displayName}
            </li>
        )
    }, [])

    return (
        <C1AutocompleteSingleAsyncHook<SearchRequestCatalogsRequest, RequestCatalog>
            {...props}
            label={props.hideLabel ? undefined : intl.formatMessage({ id: 'SelectOneCatalogHook_label', defaultMessage: 'Access profile' })}
            hook={requestCatalogsSearchApi.useLazySearchRequestCatalogs}
            Req={SearchRequestCatalogsRequest}
            renderOption={renderOption}
            renderLabel={renderLabel}
        />
    )
}
